import { localizeCatalogRowTitle } from '../i18n/catalogLabels';
import type { TranslateFn } from '../i18n/operationLabels';
import type { AppLanguage } from '../i18n/types';
import {
  ASSET_CREATE_PUBLIC_TRANSPORT_LABEL,
  isPublicTransportationFacilityName,
  linkedFacilityIdsForRow,
} from '../utils/assetCreateFacilities';
import styles from './AssetCreateFacilityCheckboxGrid.module.css';

function facilityRowNumericId(row: Record<string, unknown>): number | null {
  const raw = row.facility_id ?? row.id;
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return raw;
  }
  if (typeof raw === 'string' && raw.trim()) {
    const n = Number(raw.trim());
    if (Number.isFinite(n)) {
      return n;
    }
  }
  return null;
}

function facilityDisplayTitle(
  row: Record<string, unknown>,
  language: AppLanguage,
  t: TranslateFn,
): string {
  const fromCatalog = localizeCatalogRowTitle(row, language, t, 'facility');
  if (language === 'he' && isPublicTransportationFacilityName(fromCatalog)) {
    return 'קרוב לתחבורה ציבורית';
  }
  if (isPublicTransportationFacilityName(fromCatalog)) {
    return ASSET_CREATE_PUBLIC_TRANSPORT_LABEL;
  }
  return fromCatalog;
}

export type AssetCreateFacilityCheckboxGridProps = {
  rows: Record<string, unknown>[];
  menuId: string;
  formEpoch: number;
  sectionKey: string;
  ariaLabel: string;
  selectedIds: ReadonlySet<number>;
  language: AppLanguage;
  t: TranslateFn;
  onToggle: (facilityIds: number[], checked: boolean) => void;
};

export function AssetCreateFacilityCheckboxGrid({
  rows,
  menuId,
  formEpoch,
  sectionKey,
  ariaLabel,
  selectedIds,
  language,
  t,
  onToggle,
}: AssetCreateFacilityCheckboxGridProps) {
  const visibleRows = rows
    .map((row) => ({ row, toggleIds: linkedFacilityIdsForRow(row) }))
    .filter((entry): entry is { row: Record<string, unknown>; toggleIds: number[] } => {
      return entry.toggleIds.length > 0;
    });

  if (visibleRows.length === 0) {
    return null;
  }

  return (
    <div
      className={`${styles.facilityGrid} ${styles.facilityGridTwoCol}`}
      role="group"
      aria-label={ariaLabel}
    >
      {visibleRows.map(({ row, toggleIds }) => {
        const primaryId = toggleIds[0] ?? facilityRowNumericId(row);
        if (primaryId === null) {
          return null;
        }
        const boxId = `${menuId}-${sectionKey}-facility-${primaryId}-${formEpoch}`;
        const checked = toggleIds.every((id) => selectedIds.has(id));
        return (
          <label
            key={primaryId}
            className={`${styles.facilityBox}${checked ? ` ${styles.facilityBoxChecked}` : ''}`}
          >
            <input
              id={boxId}
              type="checkbox"
              className={styles.facilityCheckbox}
              checked={checked}
              onChange={(ev) => onToggle(toggleIds, ev.target.checked)}
            />
            <span className={styles.facilityBoxBody}>
              <span className={styles.facilityBoxTitle}>
                {facilityDisplayTitle(row, language, t)}
              </span>
            </span>
          </label>
        );
      })}
    </div>
  );
}
